// magical.ts — "Glyph Unbinding", the MAGICAL trap disarm minigame
//
// ⛔ PLAYER-FACING PROSE SAYS **THE VOID**, NEVER "the Weave" (owner, 2026-08-04). The Weave is
// Forgotten Realms' name for the fabric of magic — a third-party proper noun, and this is a
// commercial product (see CLAUDE.md's proper-noun rule). FateTide's magic is the Void. Lowercase
// "woven/threads" as a plain description of the chords is fine; naming the FIELD is not.
// (plans/TRAP_DISARM_MINIGAME_PLAN_2026-07-31.md).
//
// The discerned glyph resolves into a woven knot of luminous THREADS (chords across the glyph
// circle) crossing over/under by server-minted stack depth, feeding a pulsing charge CORE. A
// thread can be safely severed only while NOTHING uncut crosses OVER it — the game is reading
// the knot and cutting in reverse stack order. The Magic skill edge governs LEGIBILITY
// (geometry.clarity = the fraction of crossings whose over/under renders unambiguously; the rest
// draw as knots you must infer around; at edge ≥ +1 the next safe thread glows) and TIME
// (geometry.pulse_period). Cutting while the core is mid-pulse DISCHARGES the trap outright;
// the visible gather-glow in the last moments of each period is the warning to stay out.
//
// Owner rulings baked in: ONE warning surge on the first wrong cut — the thread re-knits and the
// RESERVE thread joins the knot (the knot gets harder, the "not TOO easy" tuning) — and the
// second wrong cut discharges (shell.spring()). PERFECT (natural 20) = zero wrong cuts, zero
// pulse violations, every stroke clean (crossed exactly its target thread).
//
// The cut SEQUENCE is what the server replays authoritatively (ft_trap_minigame_validate_magical)
// — this file's legality check exists only so the player gets honest feedback at cut speed.

import type { MagicGeometry, MagicThread, TrapDisarmShell } from './trap-disarm';

// Under-thread gap half-width at a clear crossing. Widened from 7 now that every thread carries a
// dark casing: the over-thread's casing eats into the gap from both sides, and at 7 it very nearly
// closed it — erasing the over/under tell that IS the puzzle.
const GAP_PX = 10;
const KNOT_R = 5;             // ambiguous-crossing knot radius

// One colour per thread. This is the fix for "I cannot tell which thread to cut, it seems super
// random" (owner, 2026-08-04): the board drew every thread in the same cyan, so with 4-8 chords
// crossing you could not trace a single one end-to-end, let alone hold it in your head while
// checking what lay over it. Reading the knot IS the game; being unable to tell the threads apart
// is not difficulty, it is illegibility.
//
// ⚠ Keyed on thread INDEX, never on depth. The server shuffles depth independently of pairing
// order precisely so the geometry carries no stack signal (ft_trap_minigame_mint_magical), so an
// index-keyed hue leaks nothing about what is over what. Colouring BY DEPTH would hand over the
// entire puzzle.
const THREAD_COLORS = [
    '#5cc8ff', '#ffd166', '#7ef0a0', '#ff8fa3',
    '#c79bff', '#ffa45c', '#6ee7e7', '#dfe4ff',
];
const RESERVE_COLOR = '#f472d0';   // the reserve stays its own colour — it arrives mid-game

function distToSegment(p: Pt, a: Pt, b: Pt): number {
    const dx = b.x - a.x, dy = b.y - a.y;
    const len2 = dx * dx + dy * dy;
    if (len2 < 1e-9) return Math.hypot(p.x - a.x, p.y - a.y);
    const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2));
    return Math.hypot(p.x - (a.x + dx * t), p.y - (a.y + dy * t));
}
const PULSE_LEADIN_S = 0.15;  // cuts landed here don't discharge, but they spoil PERFECT
const PULSE_GATHER_S = 0.6;   // visible glow ramp before the pulse — the "stay out" warning

interface Pt { x: number; y: number }

function segIntersectT(a1: Pt, a2: Pt, b1: Pt, b2: Pt): number | null {
    const d1x = a2.x - a1.x, d1y = a2.y - a1.y;
    const d2x = b2.x - b1.x, d2y = b2.y - b1.y;
    const denom = d1x * d2y - d1y * d2x;
    if (Math.abs(denom) < 1e-9) return null;
    const t = ((b1.x - a1.x) * d2y - (b1.y - a1.y) * d2x) / denom;
    const u = ((b1.x - a1.x) * d1y - (b1.y - a1.y) * d1x) / denom;
    if (t < 0 || t > 1 || u < 0 || u > 1) return null;
    return t;
}

export function mountMagicalGame(stage: HTMLElement, shell: TrapDisarmShell): void {
    const geo = shell.cfg.geometry as MagicGeometry;

    // Threads 0..N-1 are live from the start; the reserve is index N and joins on the surge —
    // index numbering MUST match ft_trap_minigame_validate_magical's replay.
    const threads: MagicThread[] = [...geo.threads, geo.reserve];
    const reserveIndex = threads.length - 1;

    stage.innerHTML = '<canvas class="ft-trap-disarm-canvas" style="width:100%;touch-action:none;display:block;"></canvas>';
    const canvas = stage.querySelector('canvas') as HTMLCanvasElement;
    const cssW = Math.max(260, Math.min(420, stage.clientWidth || 320));
    const cssH = Math.min(300, cssW);
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    canvas.width = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    canvas.style.height = cssH + 'px';
    const ctx = canvas.getContext('2d');
    if (!ctx) { shell.cancel(); return; }
    ctx.scale(dpr, dpr);

    const cx = cssW / 2;
    const cy = cssH / 2;
    const R = Math.min(cssW, cssH) / 2 - 16;
    const rim = (deg: number): Pt => ({
        x: cx + R * Math.cos((deg * Math.PI) / 180),
        y: cy + R * Math.sin((deg * Math.PI) / 180),
    });
    const ends: Array<[Pt, Pt]> = threads.map((t) => [rim(t.a1), rim(t.a2)]);

    // The old hint was one sentence and assumed you already knew the game (telling someone to cut
    // "top-down" means nothing if they have not been told the threads are layered). It never said
    // what to DO with the pointer, which is the first thing a player needs — the owner opened the
    // board and could not tell how to begin (2026-08-04). Three beats: the verb, the rule, the
    // hazard.
    shell.setHint('Drag across ONE thread to cut it. A thread can only be cut when nothing still '
        + 'crosses over the top of it — work down through the layers. Never cut while the core flares.');

    // ── knot topology (client mirror of the server's chord-crossing rule) ─────────────────────
    const cut = new Set<number>();
    let reserveActive = false;
    const liveIndexes = (): number[] => {
        const out: number[] = [];
        for (let i = 0; i < threads.length; i++) {
            if (cut.has(i)) continue;
            if (i === reserveIndex && !reserveActive) continue;
            out.push(i);
        }
        return out;
    };

    const crossPairs: Array<{ lo: number; hi: number; p: Pt; clear: boolean }> = [];
    for (let i = 0; i < threads.length; i++) {
        for (let j = i + 1; j < threads.length; j++) {
            const ti = threads[i], tj = threads[j];
            const ei = ends[i], ej = ends[j];
            if (!ti || !tj || !ei || !ej) continue;
            const t = segIntersectT(ei[0], ei[1], ej[0], ej[1]);
            if (t === null) continue;
            const p = {
                x: ei[0].x + (ei[1].x - ei[0].x) * t,
                y: ei[0].y + (ei[1].y - ei[0].y) * t,
            };
            const lower = ti.depth < tj.depth ? i : j;
            crossPairs.push({ lo: lower, hi: lower === i ? j : i, p, clear: true });
        }
    }
    // Legibility: only the first clarity-fraction of crossings render their over/under; the rest
    // draw as ambiguous knots. Deterministic pick (pair order) so a re-render never reshuffles.
    const clearCount = Math.round(Math.max(0, Math.min(1, geo.clarity)) * crossPairs.length);
    crossPairs.forEach((c, idx) => { c.clear = idx < clearCount; });

    const cuttable = (idx: number): boolean => {
        const self = threads[idx];
        if (!self) return false;
        for (const other of liveIndexes()) {
            if (other === idx) continue;
            const o = threads[other];
            if (!o || o.depth <= self.depth) continue;
            if (crossPairs.some((c) => (c.lo === idx && c.hi === other) || (c.lo === other && c.hi === idx))) {
                return false;
            }
        }
        return true;
    };

    // ── attempt state ──────────────────────────────────────────────────────────────────────────
    const cutSequence: number[] = [];
    let wrongCuts = 0;
    let cleanStrokes = true;
    let noPulseViolations = true;
    let done = false;
    let surgeFlash = 0;      // wrong-cut feedback timer
    let reserveFade = 0;     // reserve thread fade-in 0..1
    const t0 = performance.now();

    /** Pulse phase in seconds within the current period. Hot = [0, hot); gather = the tail. */
    const pulsePhase = (nowMs: number): number => ((nowMs - t0) / 1000) % geo.pulse_period;

    // ── cutting ────────────────────────────────────────────────────────────────────────────────
    let stroke: Pt[] = [];
    let stroking = false;
    let lastScratch = 0;
    // Which live threads the pointer is currently addressing — hovered when idle, actually crossed
    // once a stroke is under way. The board used to resolve a stroke only on release, so you found
    // out what you had cut after cutting it; with several chords crossing inside a few pixels that
    // is indistinguishable from the game being random.
    let aimed = new Set<number>();

    const aimAt = (path: Pt[]): Set<number> => {
        const out = new Set<number>();
        if (path.length > 1) {
            // Mid-stroke: exactly the test endStroke will apply on release.
            for (const idx of liveIndexes()) {
                const e = ends[idx];
                if (!e) continue;
                for (let k = 1; k < path.length; k++) {
                    const a = path[k - 1], b = path[k];
                    if (a && b && segIntersectT(a, b, e[0], e[1]) !== null) { out.add(idx); break; }
                }
            }
            return out;
        }
        // Idle: the nearest thread within a thumb's reach, so you can see what you are lining up on.
        const p = path[0];
        if (!p) return out;
        let best = -1;
        let bestD = 15;
        for (const idx of liveIndexes()) {
            const e = ends[idx];
            if (!e) continue;
            const d = distToSegment(p, e[0], e[1]);
            if (d < bestD) { bestD = d; best = idx; }
        }
        if (best >= 0) out.add(best);
        return out;
    };

    const pointerPos = (ev: PointerEvent): Pt => {
        const r = canvas.getBoundingClientRect();
        return { x: (ev.clientX - r.left) * (cssW / (r.width || cssW)), y: (ev.clientY - r.top) * (cssH / (r.height || cssH)) };
    };

    canvas.addEventListener('pointerdown', (ev) => {
        if (done) return;
        stroking = true;
        stroke = [pointerPos(ev)];
        try { canvas.setPointerCapture(ev.pointerId); } catch { /* non-fatal */ }
    });
    canvas.addEventListener('pointermove', (ev) => {
        if (done) return;
        const p = pointerPos(ev);
        if (!stroking) { aimed = aimAt([p]); return; }   // idle hover: what am I lining up on?
        const prev = stroke[stroke.length - 1];
        stroke.push(p);
        aimed = aimAt(stroke);

        // The athame SCRATCHES across the threads while you drag it. The board was otherwise silent
        // between cuts, so a stroke gave no feedback until it had already resolved — the magical
        // twin of the mechanical board's dragged sear (owner, 2026-08-04). Throttled and gated on
        // real travel, with rate and volume riding stroke SPEED, so a slow careful pass whispers
        // and a fast slash rasps.
        // ⛔ NO MECHANICAL FALLBACK. This used to degrade to 'creak' while trap_disarm__scratch
        // was uningested. That cue landed 2026-08-05 (8 variants), and the server stopped sending
        // 'creak' to magical boards in the same change — so the branch was already dead, but it
        // was also the last line of shipping code that could put a spring-under-load sound on an
        // arcane board. Borrowing the mechanical set is precisely what had the owner reporting the
        // magical trap "sounds like we are doing gears" (2026-08-04). If the pool is ever empty
        // the picker degrades to SILENCE, which is the honest outcome — the two games must never
        // converge on one voice.
        if (prev) {
            const travel = Math.hypot(p.x - prev.x, p.y - prev.y);
            const now = performance.now();
            if (travel > 2 && now - lastScratch > 150) {
                lastScratch = now;
                const speed = Math.max(0, Math.min(1, travel / 22));
                const vol = 0.20 + speed * 0.22;
                const rate = 0.85 + speed * 0.40 + Math.random() * 0.08;
                shell.sfx.play('scratch', vol, rate);
            }
        }
    });

    const endStroke = (): void => {
        if (!stroking || done) return;
        stroking = false;
        const path = stroke;
        stroke = [];
        aimed = new Set();   // the blade is off the threads; nothing is being addressed
        if (path.length < 2) return;

        // Which live threads did the stroke cross?
        const hit: number[] = [];
        for (const idx of liveIndexes()) {
            const e = ends[idx];
            if (!e) continue;
            let crossed = false;
            for (let s = 0; s + 1 < path.length && !crossed; s++) {
                const p1 = path[s], p2 = path[s + 1];
                if (!p1 || !p2) continue;
                crossed = segIntersectT(p1, p2, e[0], e[1]) !== null;
            }
            if (crossed) hit.push(idx);
        }
        if (hit.length === 0) return;
        if (hit.length > 1) {
            // Sloppy — a real unbinder isolates one thread per stroke. No cut, perfect spoiled.
            cleanStrokes = false;
            shell.setHint('Your stroke caught several threads at once — one thread at a time.');
            return;
        }

        const idx = hit[0];
        if (idx === undefined) return;
        const phase = pulsePhase(performance.now());
        if (phase < shell.cfg.pulse_hot_seconds) {
            // Mid-pulse cut: the core catches your blade mid-flare. Instant discharge.
            done = true;
            shell.spring();
            return;
        }
        if (phase > geo.pulse_period - PULSE_LEADIN_S) {
            noPulseViolations = false; // squeaked in under the flare — allowed, but not flawless
        }

        if (cuttable(idx)) {
            cut.add(idx);
            cutSequence.push(idx);
            shell.sfx.play('sever', 0.8, 0.95 + Math.random() * 0.1);
            if (liveIndexes().length === 0) {
                done = true;
                shell.submit({
                    cut_sequence: cutSequence,
                    clean_strokes: cleanStrokes,
                    no_pulse_violations: noPulseViolations,
                });
            }
            return;
        }

        // Wrong thread — it was load-bearing.
        wrongCuts++;
        cutSequence.push(idx); // the server replay must see the mistake too
        if (wrongCuts >= 2) {
            done = true;
            shell.spring();
            return;
        }
        // The one warning surge: the thread re-knits, and the knot thickens by the reserve.
        reserveActive = true;
        surgeFlash = 1;
        shell.sfx.play('surge', 0.85);
        shell.setHint('The Void surges and re-knits — and grows another thread. The next misread discharges it.');
    };
    canvas.addEventListener('pointerup', endStroke);
    canvas.addEventListener('pointercancel', endStroke);

    // ── render loop ────────────────────────────────────────────────────────────────────────────
    const drawThreadSegmented = (idx: number, alpha: number, hinted: boolean, aimed: boolean): void => {
        const e = ends[idx];
        if (!e) return;
        const [p1, p2] = e;
        const len = Math.hypot(p2.x - p1.x, p2.y - p1.y) || 1;
        // Gap windows where a HIGHER live thread crosses over (clear crossings only).
        const gaps: Array<[number, number]> = [];
        for (const c of crossPairs) {
            if (!c.clear || c.lo !== idx) continue;
            const other = c.hi;
            if (cut.has(other) || (other === reserveIndex && !reserveActive)) continue;
            const t = ((c.p.x - p1.x) * (p2.x - p1.x) + (c.p.y - p1.y) * (p2.y - p1.y)) / (len * len);
            gaps.push([t - GAP_PX / len, t + GAP_PX / len]);
        }
        gaps.sort((a, b) => a[0] - b[0]);

        ctx.lineCap = 'round';
        if (hinted) {
            ctx.save();
            ctx.strokeStyle = `rgba(160, 255, 200, ${alpha * 0.35})`;
            ctx.lineWidth = 9;
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
            ctx.restore();
        }
        // Build the segmented path ONCE, then stroke it twice.
        let t = 0;
        ctx.beginPath();
        for (const [gLo, gHi] of gaps) {
            const segEnd = Math.max(t, Math.min(1, gLo));
            if (segEnd > t) {
                ctx.moveTo(p1.x + (p2.x - p1.x) * t, p1.y + (p2.y - p1.y) * t);
                ctx.lineTo(p1.x + (p2.x - p1.x) * segEnd, p1.y + (p2.y - p1.y) * segEnd);
            }
            t = Math.max(t, Math.min(1, gHi));
        }
        if (t < 1) {
            ctx.moveTo(p1.x + (p2.x - p1.x) * t, p1.y + (p2.y - p1.y) * t);
            ctx.lineTo(p2.x, p2.y);
        }

        // Targeting halo: whatever the pointer is about to cut, drawn under the thread itself.
        // Pure targeting, no answer — it says WHICH thread, never whether that thread is safe.
        if (aimed) {
            ctx.save();
            ctx.strokeStyle = `rgba(255, 255, 255, ${alpha * 0.5})`;
            ctx.lineWidth = 11;
            ctx.stroke();
            ctx.restore();
        }
        // Dark casing first, so an over-thread visibly OCCLUDES the one beneath rather than merely
        // leaving a hole in it. Threads draw bottom-up, so the casing of whatever crosses over lands
        // on top of the under-thread's cut ends and the break reads as depth.
        ctx.strokeStyle = '#0b0b10';
        ctx.lineWidth = 7;
        ctx.stroke();
        const base = idx === reserveIndex
            ? RESERVE_COLOR
            : THREAD_COLORS[idx % THREAD_COLORS.length] ?? '#5cc8ff';
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.strokeStyle = base;
        ctx.lineWidth = 3;
        ctx.stroke();
        ctx.restore();
    };

    let lastTs = 0;
    const frame = (ts: number): void => {
        if (done) return;
        if (!shell.stillMounted()) {
            done = true;
            shell.cancel(true);
            return;
        }
        const dt = lastTs > 0 ? Math.min(0.1, (ts - lastTs) / 1000) : 0.016;
        lastTs = ts;
        if (reserveActive && reserveFade < 1) reserveFade = Math.min(1, reserveFade + dt * 2);
        if (surgeFlash > 0) surgeFlash = Math.max(0, surgeFlash - dt * 1.5);

        ctx.clearRect(0, 0, cssW, cssH);
                // SEMI-TRANSPARENT BASE, not opaque. The environment ground is painted behind this
        // canvas (see paintTrapGround in trap-disarm.ts); an opaque fill here would hide it
        // completely. The scrim still has to carry the mechanism's contrast, which is built
        // for a near-black base. Reviewed at gameplay scale on 2026-08-04 (the handoff's gotcha 2):
        // at 0.72 the rock read as a SURFACE and the thin sear tracks washed out against it, so the
        // scrim is heavy enough that the ground reads as grain here. The environment is shown at
        // full strength by the frame around the canvas instead — see .ft-trap-ground's padding.
        ctx.fillStyle = 'rgba(20, 20, 25, 0.89)';
        ctx.fillRect(0, 0, cssW, cssH);

        // Glyph ring
        ctx.strokeStyle = '#3a3a4a';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(cx, cy, R + 6, 0, Math.PI * 2);
        ctx.stroke();

        // Core: hot flare during the window, a visible gather-glow ramp before it.
        const phase = pulsePhase(ts);
        const hot = phase < shell.cfg.pulse_hot_seconds;
        const gather = Math.max(0, 1 - (geo.pulse_period - phase) / PULSE_GATHER_S);
        const coreR = 10 + (hot ? 10 : gather * 7);
        const coreGlow = ctx.createRadialGradient(cx, cy, 2, cx, cy, coreR * 2.6);
        const coreCol = hot ? '255, 90, 90' : surgeFlash > 0 ? '255, 140, 220' : '180, 140, 255';
        coreGlow.addColorStop(0, `rgba(${coreCol}, ${hot ? 0.95 : 0.5 + gather * 0.4})`);
        coreGlow.addColorStop(1, `rgba(${coreCol}, 0)`);
        ctx.fillStyle = coreGlow;
        ctx.beginPath();
        ctx.arc(cx, cy, coreR * 2.6, 0, Math.PI * 2);
        ctx.fill();

        // Threads, bottom-up so clear crossings read: over draws later, under carries the gap.
        const hintOn = shell.cfg.edge >= 1;
        let hintIdx = -1;
        if (hintOn) {
            for (const idx of liveIndexes()) {
                if (cuttable(idx)) { hintIdx = idx; break; }
            }
        }
        const order = liveIndexes().sort((a, b) => (threads[a]?.depth ?? 0) - (threads[b]?.depth ?? 0));
        for (const idx of order) {
            const alpha = (idx === reserveIndex ? reserveFade : 1) * (hot ? 1 : 0.85);
            drawThreadSegmented(idx, alpha, idx === hintIdx, aimed.has(idx));
        }

        // Ambiguous crossings render as knots — both live threads required, no over/under told.
        for (const c of crossPairs) {
            if (c.clear) continue;
            const live = liveIndexes();
            if (!live.includes(c.lo) || !live.includes(c.hi)) continue;
            ctx.fillStyle = hot ? 'rgba(255, 160, 160, 0.9)' : 'rgba(220, 200, 255, 0.8)';
            ctx.beginPath();
            ctx.arc(c.p.x, c.p.y, KNOT_R, 0, Math.PI * 2);
            ctx.fill();
        }

        // Live stroke feedback
        const s0 = stroke[0];
        if (stroking && s0 && stroke.length > 1) {
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
            ctx.lineWidth = 1.5;
            ctx.beginPath();
            ctx.moveTo(s0.x, s0.y);
            for (const p of stroke) ctx.lineTo(p.x, p.y);
            ctx.stroke();
        }

        ctx.fillStyle = '#9a9aa4';
        ctx.font = '10px sans-serif';
        ctx.textAlign = 'center';
        // cut.size, not a cutSequence count — a warning-surge thread appears in the sequence twice
        // (the wrong cut, then the eventual legal one) and would double-count (Codex 2026-07-31 #7).
        // Say out loud when a stroke is about to take more than one thread. That outcome (no cut,
        // perfect spoiled) used to only be explained AFTER it happened.
        if (stroking && aimed.size > 1) {
            ctx.fillStyle = '#ffb4b4';
            ctx.font = '600 10px sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText(`${aimed.size} threads — one at a time`, cx, 14);
        }

        ctx.fillStyle = '#9a9aa4';
        ctx.font = '10px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(`${cut.size} severed · ${liveIndexes().length} remain`, cx, cssH - 8);

        requestAnimationFrame(frame);
    };

    requestAnimationFrame(frame);
}
